// libs

// Components
import { makeStyles } from '@material-ui/styles'
import { prop } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import {
  getCinderQuotaUsage,
  getComputeQuotaUsage,
  getNetworkQuotaUsage,
  getSimpleTenantUsage,
} from 'account/components/userManagement/tenants/new-actions'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { type RootState, useAppSelector } from 'app/store'
import config from 'app-config'
import DocumentMeta from 'core/components/DocumentMeta'
import ExternalLink from 'core/components/ExternalLink'
import Button from 'core/elements/button/Button'
import { HeaderPrimaryActionPortal, HeaderRefreshPortal } from 'core/elements/header/portals'
import Text from 'core/elements/Text'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import useListAction from 'core/hooks/useListAction'
import { type SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import type Theme from 'core/themes/model'
import { isDevelopment, isLocalhost } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { listHypervisors, listResmgrHosts } from 'openstack/components/infrastructure/actions'
import HostsListPageHeader from 'openstack/components/infrastructure/hosts/HostsListPageHeader'
import {
  allocationCandidatesSelector,
  hypervisorsSelector,
  resmgrHostsSelector,
} from 'openstack/components/infrastructure/selectors'
import { listAllStatewiseVirtualMachinesLite } from 'openstack/components/vms/actions'
import { allStatewiseVirtualMachinesLiteSelector } from 'openstack/components/vms/selectors'
import {
  listClusterHostGroupsAllTenants,
  listClusterVMHAStatus,
} from '../infrastructure/clusters/actions'
import { VMHADisplayStatus } from '../infrastructure/clusters/model'
import {
  clusterHostGroupsAllTenantsSelector,
  clusterVMHAStatusSelector,
} from '../infrastructure/clusters/selectors'
import { listStoragePools } from '../storage/pools/actions'
import { storagePoolsSelector } from '../storage/pools/selectors'
import { QuotaCard, ResourceStatusCard } from './components'
import type { QuotaItem } from './components/QuotaCard'
import type { StatusCount } from './components/ResourceStatusCard'
import UsageMetrics from './UsageMetrics'

const DashboardPage = () => {
  const classes = useStyles()
  const { history } = useReactRouter()
  const { prefs, updatePrefs } = useScopedPreferences()
  const { allTenants } = prefs
  const { currentTenant } = prefs
  const [quotas, setQuotas] = useState<any>({})
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const {
    userDetails: { id: userId },
  } = session
  const showAdminDashboard = isAdmin()

  const { loading: loadingHypervisors } = useListAction(listHypervisors)
  const { loading: loadingResmgrHosts, reload: reloadResmgrHosts } = useListAction(listResmgrHosts)
  // PCD-11077: always the all-tenants cluster list (for non-admin callers this is just the
  // backend's default list), so host/cluster widgets resolve tenant-dedicated clusters.
  const { loading: loadingClusterHostGroups } = useListAction(listClusterHostGroupsAllTenants)

  // Get storage pools
  const { loading: loadingStoragePools, reload: reloadStoragePools } =
    useListAction(listStoragePools)
  const storagePools = useSelector(storagePoolsSelector)

  const hosts = useSelector(resmgrHostsSelector)
  const hypervisors = useAppSelector(hypervisorsSelector)
  const allocationCandidates = useAppSelector(allocationCandidatesSelector)
  const clusterHostGroups = useAppSelector(clusterHostGroupsAllTenantsSelector)
  const isVMHAEnabledForAnyCluster = useMemo(
    () => clusterHostGroups.some((cluster) => cluster?.vmHighAvailability?.enabled),
    [clusterHostGroups],
  )

  const { loading: loadingClusterVMHAStatus, reload: loadClusterVMHAStatus } = useListAction(
    listClusterVMHAStatus,
    {
      loadOnDemand: true,
      params: { clusters: clusterHostGroups },
    },
  )
  const clusterVMHAStatus = useSelector(clusterVMHAStatusSelector)

  const { reload: allReload, loading: allStatewiseVmsLoading } = useListAction(
    listAllStatewiseVirtualMachinesLite,
    {
      loadOnDemand: true,
    },
  )

  //ALL VMS Count
  const allVmsState = useAppSelector(allStatewiseVirtualMachinesLiteSelector)

  //Cluster VMHA State
  const allClustersDefaultVMHAState = {
    protected: 0,
    unprotected: 0,
    degraded: 0,
    error: 0,
    disabled: 0,
  }
  const [allClustersVMHAState, setAllClustersVMHAState] = useState(allClustersDefaultVMHAState)

  // Load VMs for all tenants
  useEffect(() => {
    // If allTenants is false, do not make any calls
    if (!allTenants) {
      return
    }

    allReload(true)
  }, [])

  // Load Cluster VMHA Status
  useEffect(() => {
    if (
      clusterHostGroups?.length === 0 ||
      loadingClusterHostGroups ||
      loadingClusterVMHAStatus ||
      clusterVMHAStatus?.length > 0
    ) {
      return
    }
    // If VMHA enable is not enable on any cluster return
    if (!isVMHAEnabledForAnyCluster) {
      setAllClustersVMHAState({
        ...allClustersDefaultVMHAState,
        disabled: clusterHostGroups?.length,
      })
      return
    }

    loadClusterVMHAStatus()
  }, [
    clusterHostGroups,
    isVMHAEnabledForAnyCluster,
    loadingClusterVMHAStatus,
    loadingClusterHostGroups,
    clusterVMHAStatus,
  ])

  //Cluster VMHA State
  useEffect(() => {
    if (!clusterHostGroups || clusterHostGroups?.length === 0 || clusterVMHAStatus?.length === 0) {
      return
    }

    const tempAllClustersVMHAState = {
      ...allClustersDefaultVMHAState,
    }

    clusterHostGroups.forEach((cluster) => {
      if (!cluster?.vmHighAvailability?.enabled) {
        tempAllClustersVMHAState.disabled += 1
      } else if (cluster?.vmhaStatus === VMHADisplayStatus.PROTECTED) {
        tempAllClustersVMHAState.protected += 1
      } else if (cluster?.vmhaStatus === VMHADisplayStatus.DEGRADED) {
        tempAllClustersVMHAState.degraded += 1
      } else if (cluster?.vmhaStatus === VMHADisplayStatus.NOT_PROTECTED) {
        tempAllClustersVMHAState.unprotected += 1
      } else {
        tempAllClustersVMHAState.disabled += 1
      }
    })

    setAllClustersVMHAState(tempAllClustersVMHAState)
  }, [clusterHostGroups, clusterVMHAStatus])

  const hypervisorCounts = useMemo(() => {
    const resmgrHostIds = hosts.map((host) => host.id)
    const validHypervisors = hypervisors.filter((hypervisor) =>
      resmgrHostIds.includes(hypervisor?.service?.host),
    )
    const counts = validHypervisors.reduce(
      (accum, hypervisor) => {
        if (hypervisor.status === 'enabled') {
          return {
            ...accum,
            success: accum.success + 1,
          }
        } else {
          return {
            ...accum,
            error: accum.error + 1,
          }
        }
      },
      {
        success: 0,
        error: 0,
      },
    )
    const hypervisorResmgrIds = validHypervisors.map((hypervisor) => hypervisor?.service?.host)
    const hypervisorHosts = hosts.filter((host) => host.displayRoles?.includes('Hypervisor'))
    const missingScheduling = hypervisorHosts.filter(
      (host) => !hypervisorResmgrIds.includes(host.id),
    )
    return {
      ...counts,
      error: counts.error + missingScheduling.length,
    }
  }, [hypervisors, hosts])

  // Status count configurations for ResourceStatusCard components
  const vmsPath = routeManager.routes.openstack.vms.path()
  const vmStatusCounts: StatusCount[] = useMemo(
    () => [
      {
        tooltip: 'Active',
        count: allVmsState?.success || 0,
        variant: 'success',
        onClick: () => history.push(`${vmsPath}?vm_state=ACTIVE`),
      },
      {
        tooltip: 'Inactive',
        count: allVmsState?.warning || 0,
        variant: 'warning',
        onClick: () => history.push(`${vmsPath}?vm_state=STOPPED`),
      },
      {
        tooltip: 'Error',
        count: allVmsState?.error || 0,
        variant: 'error',
        onClick: () => history.push(`${vmsPath}?vm_state=ERROR`),
      },
    ],
    [allVmsState, history, vmsPath],
  )

  const hostsPath = routeManager.routes.openstack.hosts.path()
  const hypervisorStatusCounts: StatusCount[] = useMemo(
    () => [
      {
        tooltip: 'Active',
        count: hypervisorCounts?.success || 0,
        variant: 'success',
        // Enabled is for checking if Scheduling is enabled on the host with hypervisor role
        onClick: () => history.push(`${hostsPath}?search=Enabled`),
      },
      {
        tooltip: 'Error',
        count: hypervisorCounts?.error || 0,
        variant: 'error',
        // Disabled is for checking if Scheduling is disabled on the host with hypervisor role
        onClick: () => history.push(`${hostsPath}?search=Disabled`),
      },
    ],
    [hypervisorCounts, history, hostsPath],
  )

  const clustersPath = routeManager.routes.openstack.clusters.path()

  const clusterVmhaStatusCounts: StatusCount[] = useMemo(
    () => [
      {
        tooltip: VMHADisplayStatus.PROTECTED,
        count: allClustersVMHAState?.protected || 0,
        variant: 'success',
        onClick: () => history.push(`${clustersPath}?search=${VMHADisplayStatus.PROTECTED}`),
      },
      {
        tooltip: VMHADisplayStatus.DEGRADED,
        count: allClustersVMHAState?.degraded || 0,
        variant: 'warning',
        onClick: () => history.push(`${clustersPath}?search=${VMHADisplayStatus.DEGRADED}`),
      },
      {
        tooltip: VMHADisplayStatus.NOT_PROTECTED,
        count: allClustersVMHAState?.unprotected || 0,
        variant: 'error',
        icon: 'times-circle',
        onClick: () =>
          history.push(
            `${clustersPath}?search=${encodeURIComponent(VMHADisplayStatus.NOT_PROTECTED)}`,
          ),
      },
      {
        tooltip: VMHADisplayStatus.DISABLED,
        count: allClustersVMHAState?.disabled || 0,
        variant: 'disabled',
        icon: 'minus-circle',
        onClick: () => history.push(`${clustersPath}?search=${VMHADisplayStatus.DISABLED}`),
      },
    ],
    [allClustersVMHAState, history, clustersPath],
  )

  // Tenant usage calculation (needed before quota configurations)
  const tenantUsage = useMemo(() => {
    return (
      (quotas?.simpleTenantUsage?.server_usages || [])?.reduce(
        (accum, usage) => {
          return {
            ...accum,
            vcpus: (accum.vcpus || 0) + usage.vcpus,
            ram: (accum.ram || 0) + usage.memory_mb,
          }
        },
        {
          vcpus: 0,
          ram: 0,
        },
      ) || { vcpus: 0, ram: 0 }
    )
  }, [quotas.simpleTenantUsage])

  // Quota configurations for QuotaCard components
  const computeQuotas: QuotaItem[] = useMemo(
    () => [
      { label: 'Cores', usage: tenantUsage?.vcpus || 0, capacity: quotas?.cores?.limit },
      { label: 'RAM', usage: tenantUsage?.ram || 0, capacity: quotas?.ram?.limit, unit: 'MiB' },
      { label: 'VMs', usage: quotas?.instances?.in_use, capacity: quotas?.instances?.limit },
    ],
    [tenantUsage, quotas],
  )

  const blockStorageQuotas: QuotaItem[] = useMemo(
    () => [
      {
        label: 'Total Block Storage',
        usage: quotas?.gigabytes?.in_use,
        capacity: quotas?.gigabytes?.limit,
        unit: 'GB',
      },
      { label: 'Volumes', usage: quotas?.volumes?.in_use, capacity: quotas?.volumes?.limit },
      {
        label: 'Volume Snapshots',
        usage: quotas?.snapshots?.in_use,
        capacity: quotas?.snapshots?.limit,
      },
    ],
    [quotas],
  )

  const networkQuotasConfig: QuotaItem[] = useMemo(
    () => [
      { label: 'Networks', usage: quotas?.network?.used, capacity: quotas?.network?.limit },
      { label: 'Subnets', usage: quotas?.subnet?.used, capacity: quotas?.subnet?.limit },
      { label: 'Routers', usage: quotas?.router?.used, capacity: quotas?.router?.limit },
      { label: 'Ports', usage: quotas?.port?.used, capacity: quotas?.port?.limit },
      { label: 'Public IPs', usage: quotas?.floatingip?.used, capacity: quotas?.floatingip?.limit },
      {
        label: 'Security Groups',
        usage: quotas?.security_group?.used,
        capacity: quotas?.security_group?.limit,
      },
      {
        label: 'Security Group Rules',
        usage: quotas?.security_group_rule?.used,
        capacity: quotas?.security_group_rule?.limit,
      },
    ],
    [quotas],
  )

  useEffect(() => {
    if (!userId || !currentTenant) {
      return
    }
    const makeCalls = async () => {
      let computeQuotasData = {}
      let cinderQuotasData = {}
      let networkQuotasData = {}
      let simpleTenantUsage = {}
      try {
        computeQuotasData = await getComputeQuotaUsage({ tenantId: currentTenant })
      } catch (err) {
        console.error(err)
      }
      try {
        cinderQuotasData = await getCinderQuotaUsage({ tenantId: currentTenant })
      } catch (err) {
        console.error(err)
      }
      try {
        networkQuotasData = await getNetworkQuotaUsage({ tenantId: currentTenant })
      } catch (err) {
        console.error(err)
      }
      try {
        simpleTenantUsage = await getSimpleTenantUsage({ tenantId: currentTenant })
      } catch (err) {
        console.error(err)
      }
      setQuotas({
        ...computeQuotasData,
        ...cinderQuotasData,
        ...networkQuotasData,
        simpleTenantUsage,
      })
    }
    makeCalls()
  }, [userId, currentTenant])

  // Create Grafana URL
  const grafanaURL = useMemo(() => {
    if (isDevelopment() || isLocalhost()) {
      return `${config.apiHost}/grafana/login`
    }
    return `${window.location.origin}/grafana/login`
  }, [])

  const handleAllTenantsToggle = useCallback(() => {
    updatePrefs({ allTenants: !allTenants })

    // If toggled to true, load all tenants data.
    if (!allTenants) {
      allReload(true)
    }
  }, [allTenants, updatePrefs, allReload])

  const handleReload = useCallback(() => {
    if (allTenants) {
      allReload(true)
    }
    if (isVMHAEnabledForAnyCluster) {
      loadClusterVMHAStatus(true)
    }
    reloadResmgrHosts(true)
    reloadStoragePools(true)
  }, [allTenants, allReload, loadClusterVMHAStatus, reloadResmgrHosts, isVMHAEnabledForAnyCluster])

  return (
    <>
      <DocumentMeta title="Dashboard" />
      {/* <ContentTopHeaderPortal>
        <Info primary>
        </Info>
      </ContentTopHeaderPortal> */}
      {/* Show Grafana link only when allTenants is true */}
      <HeaderRefreshPortal>
        <div className={classes.headerRefreshPortal}>
          {allTenants ? (
            <>
              <Text variant="subtitle2">Resource Utilization</Text>
              <Button
                variant="secondary"
                icon="sync"
                onClick={handleReload}
                loading={
                  loadingClusterVMHAStatus ||
                  allStatewiseVmsLoading ||
                  loadingHypervisors ||
                  loadingResmgrHosts
                }
              >
                Refresh
              </Button>
            </>
          ) : (
            <Text variant="subtitle2">Tenant Quota Usage </Text>
          )}
        </div>
      </HeaderRefreshPortal>
      <HeaderPrimaryActionPortal>
        {/* Just remove this allTenants condition if we decide to show grafana link for tenant only */}
        {allTenants && (
          <>
            <ExternalLink textVariant="caption1" url={grafanaURL} className={classes.externalLink}>
              View Grafana Dashboard
            </ExternalLink>
            <div className={classes.dividerTop} />
          </>
        )}
        {showAdminDashboard ? (
          <>
            <ToggleSwitch
              active={allTenants as boolean}
              onClick={handleAllTenantsToggle}
              label="Show All Tenants Info"
            />
          </>
        ) : null}
      </HeaderPrimaryActionPortal>
      {allTenants && (
        <div className={classes.resourceStatuses}>
          {/* Virtual Machines */}
          <ResourceStatusCard
            title="Virtual Machines"
            linkText="See All"
            onLinkClick={() => history.push(routeManager.routes.openstack.vms.path())}
            loading={allStatewiseVmsLoading}
            statusCounts={vmStatusCounts}
          />
          {/* Hypervisors */}
          {showAdminDashboard && (
            <ResourceStatusCard
              title="Hypervisors"
              linkText="See All"
              onLinkClick={() => history.push(routeManager.routes.openstack.hosts.path())}
              statusCounts={hypervisorStatusCounts}
              loading={loadingHypervisors || loadingResmgrHosts}
            />
          )}
          {/* Cluster VMHA Status */}
          <ResourceStatusCard
            title="Cluster VM HA Status"
            linkText="See All"
            onLinkClick={() => history.push(routeManager.routes.openstack.clusters.path())}
            loading={loadingClusterHostGroups || loadingClusterVMHAStatus}
            statusCounts={clusterVmhaStatusCounts}
          />
        </div>
      )}
      {/* Show Quotas only when allTenants is false */}
      {!allTenants ? (
        <div className={classes.cards}>
          <QuotaCard title="Compute Quotas" icon="calculator" quotas={computeQuotas} />
          <QuotaCard title="Block Storage Quotas" icon="database" quotas={blockStorageQuotas} />
          <QuotaCard title="Network Quotas" icon="server" quotas={networkQuotasConfig} />
        </div>
      ) : (
        <>
          {/* <Text variant="subtitle2">Compute</Text>
          <Divider className={classes.horizontalDivider} /> */}
          <HostsListPageHeader
            hosts={hosts}
            hypervisors={hypervisors}
            storagePools={storagePools}
            allocationCandidates={allocationCandidates}
            showAllocation
          />
          <UsageMetrics />
        </>
      )}
    </>
  )
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  cards: {
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
  },
  resourceStatuses: {
    marginBottom: 24,
    display: 'flex',
    gap: 8,
  },
  dividerTop: {
    width: 1,
    height: 16,
    background: theme.components.card.text,
  },
  externalLink: {
    width: 'fit-content',
  },
  horizontalDivider: {
    margin: theme.spacing(1, 0, 3),
  },
  headerRefreshPortal: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'auto auto',
    alignItems: 'center',
  },
}))

export default DashboardPage
